import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  alpha,
  Stack,
} from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { startChangePassword, startGetApplications } from '../store/password';
import { SnackAlert } from './SnackAlert';
import InputField from './formFields/InputField';
import AutocompleteField from './formFields/autocompleteField';
import QiPasswordField from './formFields/QiPasswordField';

interface Props {
  open: boolean;
  onClose: () => void;
  employeeId?: string;
  userId?: string;
  employeeName?: string;
}

const PasswordRequirement = ({ label, met }: { label: string; met: boolean }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
    {met ? (
      <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
    ) : (
      <Cancel sx={{ fontSize: 16, color: 'error.main', opacity: 0.5 }} />
    )}
    <Typography
      variant="caption"
      sx={{ color: met ? 'success.main' : 'text.secondary', fontWeight: met ? 600 : 400 }}
    >
      {label}
    </Typography>
  </Stack>
);

const passwordRequirements = [
  { label: 'Mínimo 8 caracteres', regex: /.{8,}/ },
  { label: 'Al menos una mayúscula', regex: /[A-Z]/ },
  { label: 'Al menos una minúscula', regex: /[a-z]/ },
  { label: 'Al menos un número', regex: /[0-9]/ },
  { label: 'Al menos un carácter especial', regex: /[!@#$%^&*(),.?":{}|<>]/ },
];

export const ChangePasswordModal = ({ open, onClose, employeeId, userId, employeeName }: Props) => {
  const dispatch: any = useDispatch();
  const { aplications_select_list } = useSelector((state: any) => state.password);
  const [alert, setAlert] = React.useState({ open: false, msg: '', status: 'success' });

  React.useEffect(() => {
    if (open) {
      dispatch(startGetApplications());
    }
  }, [open]);

  const initialValues = {
    password: '',
    confirmPassword: '',
    application: null,
  };

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required('La contraseña es obligatoria')
      .min(8, 'La contraseña debe tener al menos 8 caracteres')
      .matches(/[A-Z]/, 'Debe tener al menos una mayúscula')
      .matches(/[a-z]/, 'Debe tener al menos una minúscula')
      .matches(/[0-9]/, 'Debe tener al menos un número')
      .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Debe tener al menos un carácter especial'),
    confirmPassword: Yup.string()
      .required('Debes confirmar la contraseña')
      .oneOf([Yup.ref('password')], 'Las contraseñas no coinciden'),
    application: Yup.object().required('Debes seleccionar una opción').nullable(),
  });

  const handleSubmit = async (values: any, { setSubmitting, resetForm }: any) => {
    const payload: any = {
      password: values.password,
      applicationId: values.application.id || values.application.value,
    };

    if (employeeId) payload.employeeId = employeeId;
    if (userId) payload.userId = userId;

    const response = await dispatch(startChangePassword(payload));

    if (response.ok) {
      setAlert({ open: true, msg: 'Contraseña cambiada con éxito', status: 'success' });
      setTimeout(() => {
        onClose();
        resetForm();
      }, 1500);
    } else {
      setAlert({
        open: true,
        msg: response.errors?.[0] || 'Error al cambiar contraseña',
        status: 'error',
      });
    }
    setSubmitting(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{ fontWeight: 700, color: 'primary.main', bgcolor: alpha('#663cc1', 0.05) }}
        >
          Cambiar Contraseña {employeeName ? `- ${employeeName}` : ''}
        </DialogTitle>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values }) => (
            <Form autoComplete="none">
              {/* Campos falsos invisibles para engañar al gestor de contraseñas del navegador */}
              <input
                type="text"
                name="fake_user_name"
                style={{ display: 'none' }}
                tabIndex={-1}
                autoComplete="none"
              />
              <input
                type="password"
                name="fake_password_field"
                style={{ display: 'none' }}
                tabIndex={-1}
                autoComplete="none"
              />

              <DialogContent sx={{ mt: 2 }}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12 }}>
                    <AutocompleteField
                      name="application"
                      label="Aplicación"
                      options={aplications_select_list || []}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <QiPasswordField name="password" label="Nueva Contraseña" fullWidth />
                    <Box sx={{ mt: 1.5, px: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, display: 'block', mb: 1, color: 'text.secondary' }}
                      >
                        Requisitos de seguridad:
                      </Typography>
                      {passwordRequirements.map((req, index) => (
                        <PasswordRequirement
                          key={index}
                          label={req.label}
                          met={req.regex.test(values.password)}
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <QiPasswordField
                      name="confirmPassword"
                      label="Confirmar Nueva Contraseña"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ p: 2, bgcolor: alpha('#000', 0.02) }}>
                <Button onClick={onClose} color="secondary">
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{ bgcolor: '#663cc1', '&:hover': { bgcolor: '#5532a3' } }}
                >
                  {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
      <SnackAlert
        open={alert.open}
        onclick={(val: boolean) => setAlert({ ...alert, open: val })}
        text={alert.msg}
        severity={alert.status}
      />
    </>
  );
};
